import { useState, useEffect, useMemo } from "react";
import { API_URL } from "../data/config";
import { Zap, Flame, ChevronRight, ChevronDown, ArrowRight, Sparkles, TrendingUp, Activity, Globe, Plus, Check, Database } from "lucide-react";

// =============================================================================
// INSIGHTS — competitor intercept intelligence (v7.18.1)
// =============================================================================

interface InsightsProps {
  onAddToPipeline?: (leads: any[]) => void;
  onViewPermits?: (city: string, province: string) => void;
  currentCity?: string | null;
  currentState?: string | null;
}

const T = {
  text:        "#0F0F1A",
  textMid:     "#3A3A4F",
  textSub:     "#54546A",
  textFade:    "#8A8A9E",
  primary:     "#FF8800",
  primaryDark: "#E07700",
  primaryLight:"#FFF7ED",
  bg:          "#FFFFFF",
  bgOff:       "#FAFAFA",
  bgSub:       "#F5F5F5",
  border:      "#E5E5EA",
  borderSoft:  "#F0F0F4",
  red:         "#EF4444",
  redDark:     "#991B1B",
  redBg:       "#FEF2F2",
  redBgSolid:  "#FEE2E2",
  redBorder:   "#FCA5A5",
  green:       "#10B981",
  greenDark:   "#166534",
  greenBg:     "#DCFCE7",
  greenBorder: "#86EFAC",
};

export default function SolarLeadInsights({ onAddToPipeline, onViewPermits, currentCity, currentState }: InsightsProps) {
  // Data state
  const [scale, setScale] = useState<any>(null);
  const [competitors, setCompetitors] = useState<any[]>([]);
  const [competitorsMeta, setCompetitorsMeta] = useState<any>({ totalPermitsAnalyzed: 0, days: 90, widened: false });
  const [freshLeads, setFreshLeads] = useState<any[]>([]);
  const [marketsMoving, setMarketsMoving] = useState<any[]>([]);
  const [marketsRankedBy, setMarketsRankedBy] = useState<string>("growth");
  const [loading, setLoading] = useState(true);

  // UI state
  const [expandedContractor, setExpandedContractor] = useState<string | null>(null);
  const [freshExpanded, setFreshExpanded] = useState(false);
  const [addedIds, setAddedIds] = useState<Set<any>>(new Set());

  // Drill-down modal state — full permit list for one contractor
  const [drillContractor, setDrillContractor] = useState<string | null>(null);
  const [drillLeads, setDrillLeads] = useState<any[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  // Pricing Pulse — 20-year trend data from solar_installations (3.58M dataset)
  const [pricingTrends, setPricingTrends] = useState<any[]>([]);
  const [pricingOverview, setPricingOverview] = useState<any>(null);

  // Fresh Roofs — recent re-roofs as solar prospects (from roofing_permits ~65K rows)
  const [freshRoofs, setFreshRoofs] = useState<any[]>([]);
  const [freshRoofsTotal, setFreshRoofsTotal] = useState(0);
  const [copiedAddr, setCopiedAddr] = useState<string | null>(null);

  // Roofing coverage indicator — tells reps what % of their territory has
  // roofer info attached (vs. address-only). Powers the honesty banner.
  const [roofingCoverage, setRoofingCoverage] = useState<any>(null);

  // Roofer leaderboard — top contractors active in territory by permit volume.
  // Only renders when coverage is high (≥30% of records have contractor names).
  const [rooferLeaderboard, setRooferLeaderboard] = useState<any[]>([]);
  const [copiedRooferContact, setCopiedRooferContact] = useState<string | null>(null);

  // Fetch drill-down data when a contractor is selected
  useEffect(() => {
    if (!drillContractor) { setDrillLeads([]); return; }
    let active = true;
    setDrillLoading(true);
    fetch(`${API_URL}/api/insights/competitor-permits?contractor=${encodeURIComponent(drillContractor)}&limit=500`)
      .then(r => r.json())
      .then(d => {
        if (!active) return;
        setDrillLeads(d?.leads || []);
        setDrillLoading(false);
      })
      .catch(() => { if (active) setDrillLoading(false); });
    return () => { active = false; };
  }, [drillContractor]);

  // Fetch all insights data on mount
  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/insights/scale`).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/insights/competitor-moves?days=90&limit=10&samples=8`).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/insights/fresh-permits?limit=100`).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/insights/markets-moving`).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/market/pricing`).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/market/overview`).then(r => r.json()).catch(() => null),
    ]).then(([scaleRes, compRes, freshRes, marketsRes, pricingRes, overviewRes]) => {
      if (!active) return;
      if (scaleRes?.success) setScale(scaleRes);
      if (compRes?.success && Array.isArray(compRes.competitors)) {
        setCompetitors(compRes.competitors);
        setCompetitorsMeta({
          totalPermitsAnalyzed: compRes.totalPermitsAnalyzed || 0,
          // Use ?? so 0 (all-time) is preserved, not replaced with 90
          days: compRes.days ?? 90,
          widened: !!compRes.widened,
        });
      }
      setFreshLeads(freshRes?.leads || []);
      if (marketsRes?.success && Array.isArray(marketsRes.markets)) {
        setMarketsMoving(marketsRes.markets);
        if (marketsRes.markets[0]?.rankedBy) setMarketsRankedBy(marketsRes.markets[0].rankedBy);
      }
      if (pricingRes?.success && Array.isArray(pricingRes.trends)) setPricingTrends(pricingRes.trends);
      if (overviewRes?.success && overviewRes.data) setPricingOverview(overviewRes.data);
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  // Fetch Fresh Roofs + coverage + leaderboard scoped to territory.
  // All three re-fire when the rep changes territory; they share the same
  // dependency since they're conceptually one feature.
  useEffect(() => {
    let active = true;
    const params = new URLSearchParams({ days: "90", limit: "50" });
    if (currentCity) params.set("city", currentCity);
    if (currentState) params.set("state", currentState);
    const territoryParams = new URLSearchParams();
    if (currentCity) territoryParams.set("city", currentCity);
    if (currentState) territoryParams.set("state", currentState);

    Promise.all([
      fetch(`${API_URL}/api/roofing/prospects?${params}`).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/roofing/coverage?${territoryParams}`).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/roofing/leaderboard?${territoryParams}&days=180&limit=10`).then(r => r.json()).catch(() => null),
    ]).then(([prospectsRes, coverageRes, leaderboardRes]) => {
      if (!active) return;
      if (prospectsRes?.success) {
        setFreshRoofs(prospectsRes.prospects || []);
        setFreshRoofsTotal(prospectsRes.total || 0);
      }
      if (coverageRes?.success) setRoofingCoverage(coverageRes);
      if (leaderboardRes?.success) setRooferLeaderboard(leaderboardRes.leaderboard || []);
    });
    return () => { active = false; };
  }, [currentCity, currentState]);

  // Dynamic month name for Fresh Permits header
  const monthName = new Date().toLocaleString("en-US", { month: "long" });

  // Format large numbers with thousand separators and K/M suffix
  const fmtScale = (n: number) => {
    if (!n) return "0";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
    return n.toLocaleString();
  };

  // Derived
  const uniqueCompetitors = competitors.length;
  const totalCompetitorPermits = useMemo(
    () => competitors.reduce((sum, c) => sum + (c.permitCount || 0), 0),
    [competitors]
  );

  const windowLabel = competitorsMeta.days === 0
    ? "Top installers in your markets"
    : competitorsMeta.days >= 365
    ? "Major players in your markets"
    : competitorsMeta.days >= 180
    ? "Active installers · last 6 months"
    : "Competitor moves · intercept window";

  const windowSub = competitorsMeta.days === 0
    ? "Top contractors by recent permit volume"
    : competitorsMeta.days >= 365
    ? "Top contractors by recent permit volume"
    : competitorsMeta.days >= 180
    ? "Active contractors with recent permits"
    : "Who's filing · call homeowner before install locks in";

  // Handlers
  const handleAddContractorToPipeline = (contractor: any) => {
    if (!onAddToPipeline) return;
    const leads = (contractor.sampleLeads || []).map((l: any) => ({
      ...l, source: 'ai-insights', contractorName: contractor.name, stage: 'new',
    }));
    onAddToPipeline(leads);
  };

  const handleAddSingleLead = (lead: any, contractorName?: string) => {
    if (!onAddToPipeline) return;
    onAddToPipeline([{ ...lead, source: 'ai-insights', contractorName: contractorName || null, stage: 'new' }]);
    setAddedIds(prev => { const next = new Set(prev); next.add(lead.id); return next; });
    setTimeout(() => {
      setAddedIds(prev => { const next = new Set(prev); next.delete(lead.id); return next; });
    }, 3000);
  };

  const handleAddAllFresh = () => {
    if (!onAddToPipeline || freshLeads.length === 0) return;
    const leads = freshLeads.map((l: any) => ({
      ...l,
      source: 'ai-insights',
      contractorName: l.contractorName || l.contractor_name || null,
      stage: 'new',
    }));
    onAddToPipeline(leads);
  };

  const fmtMoney = (n: number) => {
    if (!n || n < 1000) return null;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
    return `$${n}`;
  };

  // Fresh permits — collapsed shows 8, expanded shows all
  const visibleFreshCount = freshExpanded ? freshLeads.length : Math.min(8, freshLeads.length);

  return (
    <div style={{ flex: 1, background: T.bgOff, overflowY: "auto" }}>
      {/* ═══ HEADER ═══ */}
      <div style={{ background: T.bg, padding: "32px 40px 24px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "2.5px", textTransform: "uppercase", color: T.red, marginBottom: 10 }}>
          ● INTERCEPT INTELLIGENCE
        </div>
        <h1 style={{ fontFamily: "'Outfit',sans-serif", fontSize: 36, fontWeight: 900, color: T.text, letterSpacing: "-1.5px", lineHeight: 1.05, margin: 0, marginBottom: 8 }}>
          Who's filing in your markets. <span style={{ color: T.primary }}>Right now.</span>
        </h1>
        <div style={{ fontSize: 14, color: T.textSub, maxWidth: 720, marginBottom: 24 }}>
          Every new permit is a ~60 day window to call the homeowner and undercut before install locks in. Below: who, where, and every address you can intercept.
        </div>

        {/* ═══ PLATFORM SCALE STRIP ═══ */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
          {/* Permits tracked */}
          <div style={{ padding: "18px 20px", background: T.bgOff, borderRadius: 12, border: `1px solid ${T.borderSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Activity size={14} color={T.primary} />
              <span style={{ fontSize: 11, fontWeight: 800, color: T.textSub, textTransform: "uppercase", letterSpacing: "1.2px" }}>Permits tracked</span>
            </div>
            {loading || !scale ? (
              <div style={{ fontSize: 13, color: T.textFade }}>Loading...</div>
            ) : (
              <>
                <div style={{ fontFamily: "'Outfit'", fontSize: 34, fontWeight: 900, color: T.text, letterSpacing: "-1.5px", lineHeight: 1 }}>
                  {fmtScale(scale.totalPermits)}
                </div>
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 8, fontWeight: 600 }}>
                  {scale.totalPermits.toLocaleString()} homes with verified solar activity
                </div>
              </>
            )}
          </div>

          {/* Data sources */}
          <div style={{ padding: "18px 20px", background: T.bgOff, borderRadius: 12, border: `1px solid ${T.borderSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Database size={14} color={T.primary} />
              <span style={{ fontSize: 11, fontWeight: 800, color: T.textSub, textTransform: "uppercase", letterSpacing: "1.2px" }}>Data sources</span>
            </div>
            <div style={{ fontFamily: "'Outfit'", fontSize: 34, fontWeight: 900, color: T.text, letterSpacing: "-1.5px", lineHeight: 1 }}>
              340+
            </div>
            <div style={{ fontSize: 11, color: T.textSub, marginTop: 8, fontWeight: 600 }}>
              live municipal + state feeds, US + Canada
            </div>
          </div>

          {/* Cities tracked */}
          <div style={{ padding: "18px 20px", background: T.redBg, borderRadius: 12, border: `1px solid ${T.redBorder}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Globe size={14} color={T.red} />
              <span style={{ fontSize: 11, fontWeight: 800, color: T.redDark, textTransform: "uppercase", letterSpacing: "1.2px" }}>Cities tracked</span>
            </div>
            {loading || !scale ? (
              <div style={{ fontSize: 13, color: T.textFade }}>Loading...</div>
            ) : (
              <>
                <div style={{ fontFamily: "'Outfit'", fontSize: 34, fontWeight: 900, color: T.redDark, letterSpacing: "-1.5px", lineHeight: 1 }}>
                  {(scale.citiesTracked || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: T.redDark, marginTop: 8, fontWeight: 600 }}>
                  municipalities with verified solar permit data
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ═══ BODY ═══ */}
      <div style={{ padding: "24px 40px 40px" }}>

        {/* ─── FRESH PERMITS (full width, first) ─── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, padding: "20px 22px", display: "flex", flexDirection: "column", marginBottom: 20, boxShadow: "0 1px 3px rgba(15,15,26,0.04)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: T.redBg, border: `1px solid ${T.redBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Zap size={16} color={T.red} />
              </div>
              <div>
                <div style={{ fontFamily: "'Outfit'", fontSize: 16, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>Fresh permits</div>
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>
                  Filed in {monthName} · click to intercept
                  {freshLeads.length > 0 && <span> · <span style={{ fontWeight: 700, color: T.textMid }}>{freshLeads.length.toLocaleString()} active</span></span>}
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ color: T.textSub, fontSize: 13, padding: "20px 0" }}>Loading...</div>
          ) : freshLeads.length === 0 ? (
            <div style={{ color: T.textSub, fontSize: 13, padding: "20px 0" }}>No permits filed in {monthName} yet.</div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                {freshLeads.slice(0, visibleFreshCount).map((lead: any, i: number) => {
                  const permitDateStr = lead.permitDate || lead.permit_date;
                  let days = 0;
                  if (permitDateStr) {
                    const diff = Date.now() - new Date(permitDateStr).getTime();
                    days = Math.max(0, Math.floor(diff / 86400000));
                  }
                  const contractor = lead.contractorName || lead.contractor_name;
                  const isAdded = addedIds.has(lead.id);
                  const fresh = days <= 7;
                  return (
                    <div
                      key={lead.id || i}
                      onClick={() => handleAddSingleLead(lead, contractor)}
                      style={{
                        padding: "10px 12px", borderRadius: 10,
                        background: isAdded ? T.greenBg : fresh ? T.redBg : T.bgSub,
                        display: "flex", alignItems: "center", gap: 10,
                        cursor: "pointer",
                        border: isAdded ? `1px solid ${T.greenBorder}` : "1px solid transparent",
                        transition: "background 0.15s, border-color 0.15s",
                      }}
                      onMouseEnter={(e: any) => { if (!isAdded) e.currentTarget.style.borderColor = fresh ? T.redBorder : T.border; }}
                      onMouseLeave={(e: any) => { if (!isAdded) e.currentTarget.style.borderColor = "transparent"; }}>
                      <div style={{ width: 26, height: 26, borderRadius: 6, background: fresh ? T.red : T.text, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, flexShrink: 0 }}>
                        {fresh ? "⚡" : `${days}d`}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lead.address}</div>
                        <div style={{ fontSize: 11, color: T.textSub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {lead.city}{(lead.province || lead.state) ? `, ${lead.province || lead.state}` : ''}
                          {contractor
                            ? <> · <span style={{ color: T.textFade }}>installer:</span> <span style={{ fontWeight: 700, color: T.redDark }}>{contractor}</span></>
                            : null
                          }
                        </div>
                      </div>
                      {isAdded ? (
                        <div style={{ padding: "4px 9px", borderRadius: 5, background: T.green, color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
                          <Check size={11} /> Added
                        </div>
                      ) : (
                        <div style={{ padding: "4px 9px", borderRadius: 5, background: T.text, color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 3, flexShrink: 0, opacity: 0.85 }}>
                          <Plus size={11} /> Add
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action row */}
              {onAddToPipeline && (
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  {freshLeads.length > 8 && (
                    <button
                      onClick={() => setFreshExpanded(!freshExpanded)}
                      style={{ flex: 1, padding: "10px 12px", borderRadius: 10, background: T.bg, color: T.text, border: `1.5px solid ${T.border}`, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}
                      onMouseEnter={(e: any) => e.currentTarget.style.borderColor = T.text}
                      onMouseLeave={(e: any) => e.currentTarget.style.borderColor = T.border}>
                      {freshExpanded ? `Collapse` : `See all ${freshLeads.length}`}
                    </button>
                  )}
                  <button
                    onClick={handleAddAllFresh}
                    style={{ flex: 1, padding: "10px 12px", borderRadius: 10, background: T.primary, color: "#fff", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                    onMouseEnter={(e: any) => e.currentTarget.style.background = T.primaryDark}
                    onMouseLeave={(e: any) => e.currentTarget.style.background = T.primary}>
                    Add all {freshLeads.length} <ArrowRight size={13} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ─── HERO: COMPETITOR MOVES ─── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden", marginBottom: 20, boxShadow: "0 1px 3px rgba(15,15,26,0.04)" }}>
          <div style={{ padding: "18px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: T.redBg, border: `1px solid ${T.redBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Flame size={16} color={T.red} />
              </div>
              <div>
                <div style={{ fontFamily: "'Outfit'", fontSize: 16, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>{windowLabel}</div>
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>
                  {windowSub}
                  {uniqueCompetitors > 0 && <span> · <span style={{ fontWeight: 700, color: T.textMid }}>{uniqueCompetitors} contractors tracked</span></span>}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: T.textSub, fontWeight: 600 }}>
              {totalCompetitorPermits.toLocaleString()} competitor permits{scale?.totalPermits ? <span style={{ color: T.textFade }}> · from {fmtScale(scale.totalPermits)}+ tracked</span> : null}
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: T.textSub, fontSize: 13 }}>Loading competitor intel...</div>
          ) : competitors.length === 0 ? (
            <div style={{ padding: "32px 40px", textAlign: "center" }}>
              <div style={{ fontSize: 14, color: T.textMid, fontWeight: 700, marginBottom: 6 }}>Contractor data expanding soon.</div>
              <div style={{ fontSize: 12, color: T.textSub, maxWidth: 560, margin: "0 auto", lineHeight: 1.5 }}>
                Municipal permits don't always publish installer names. Contractor identification is expanding across all 340+ scrapers. In the meantime, every fresh permit below is a homeowner who just filed — call before install.
              </div>
            </div>
          ) : (
            <div>
              {competitors.map((c: any, i: number) => {
                const expanded = expandedContractor === c.name;
                const pinColor = i === 0 ? T.red : i < 3 ? T.text : T.textMid;
                return (
                  <div key={`${c.name}-${i}`} style={{ borderBottom: i < competitors.length - 1 ? `1px solid ${T.borderSoft}` : "none" }}>
                    <div
                      onClick={() => setExpandedContractor(expanded ? null : c.name)}
                      style={{ padding: "16px 24px", display: "flex", alignItems: "center", gap: 16, cursor: "pointer", transition: "background 0.1s", background: expanded ? T.bgOff : T.bg }}
                      onMouseEnter={(e: any) => { if (!expanded) e.currentTarget.style.background = T.bgOff; }}
                      onMouseLeave={(e: any) => { if (!expanded) e.currentTarget.style.background = T.bg; }}>

                      <div style={{ width: 28, height: 28, borderRadius: 7, background: pinColor, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, flexShrink: 0 }}>
                        {i + 1}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                          <span style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 800, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {c.name}
                          </span>
                          {c.freshestDays !== null && c.freshestDays <= 14 && (
                            <span style={{ padding: "2px 8px", borderRadius: 5, background: T.redBgSolid, border: `1px solid ${T.redBorder}`, fontSize: 10, fontWeight: 800, color: T.redDark, flexShrink: 0 }}>
                              ⚡ fresh {c.freshestDays}d
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: T.textSub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {c.topCities.map((tc: any, idx: number) => (
                            <span key={idx}>
                              <span style={{ fontWeight: 700, color: T.textMid }}>{tc.city}{tc.province ? `, ${tc.province}` : ''}</span>
                              <span style={{ color: T.textFade }}> ({tc.count})</span>
                              {idx < c.topCities.length - 1 ? <span style={{ color: T.textFade }}> · </span> : null}
                            </span>
                          ))}
                          {c.remainingCities > 0 && <span style={{ color: T.textFade }}> · +{c.remainingCities} more</span>}
                        </div>
                      </div>

                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Outfit'", fontSize: 22, fontWeight: 900, color: T.text, lineHeight: 1 }}>{c.permitCount}</div>
                        <div style={{ fontSize: 10, color: T.textSub, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>permits</div>
                      </div>

                      {fmtMoney(c.totalValueEstimate) && (
                        <div style={{ textAlign: "right", flexShrink: 0, width: 70, borderLeft: `1px solid ${T.borderSoft}`, paddingLeft: 16 }}>
                          <div style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 800, color: T.green, lineHeight: 1 }}>{fmtMoney(c.totalValueEstimate)}</div>
                          <div style={{ fontSize: 10, color: T.textSub, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>est. value</div>
                        </div>
                      )}

                      <div style={{ flexShrink: 0, color: T.textFade }}>
                        {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </div>
                    </div>

                    {expanded && (
                      <div style={{ background: T.bgOff, borderTop: `1px solid ${T.borderSoft}`, padding: "14px 24px 18px" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: 10 }}>
                          Sample addresses · {c.sampleLeads.length} of {c.permitCount} shown
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {c.sampleLeads.map((lead: any, idx: number) => {
                            const days = lead.days_old || 0;
                            const fresh = days <= 30;
                            const isAdded = addedIds.has(lead.id);
                            return (
                              <div
                                key={lead.id || idx}
                                onClick={(e: any) => { e.stopPropagation(); handleAddSingleLead(lead, c.name); }}
                                style={{ padding: "10px 14px", background: isAdded ? T.greenBg : T.bg, borderRadius: 8, border: `1px solid ${isAdded ? T.greenBorder : T.borderSoft}`, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", transition: "background 0.15s, border-color 0.15s" }}
                                onMouseEnter={(e: any) => { if (!isAdded) e.currentTarget.style.borderColor = T.text; }}
                                onMouseLeave={(e: any) => { if (!isAdded) e.currentTarget.style.borderColor = T.borderSoft; }}>
                                <div style={{ width: 32, height: 32, borderRadius: 6, background: fresh ? T.redBgSolid : T.bgSub, color: fresh ? T.redDark : T.textMid, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, flexShrink: 0 }}>
                                  {days <= 7 ? "⚡" : `${days}d`}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lead.address}</div>
                                  <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
                                    {lead.city}{lead.province ? `, ${lead.province}` : ''} · filed {days}d ago
                                    {lead.permit_value > 0 && <span> · {fmtMoney(lead.permit_value)}</span>}
                                  </div>
                                </div>
                                <div style={{ padding: "6px 12px", borderRadius: 6, background: isAdded ? T.green : T.text, color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                                  {isAdded ? <><Check size={12} /> Added</> : <><Plus size={12} /> Intercept</>}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {onAddToPipeline && c.sampleLeads.length > 0 && (
                          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                            <button
                              onClick={(e: any) => { e.stopPropagation(); handleAddContractorToPipeline(c); }}
                              style={{ padding: "10px 16px", borderRadius: 8, background: T.primary, color: "#fff", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 6 }}
                              onMouseEnter={(e: any) => e.currentTarget.style.background = T.primaryDark}
                              onMouseLeave={(e: any) => e.currentTarget.style.background = T.primary}>
                              Intercept all {c.sampleLeads.length} <ArrowRight size={13} />
                            </button>
                            {c.permitCount > c.sampleLeads.length && (
                              <button
                                onClick={(e: any) => { e.stopPropagation(); setDrillContractor(c.name); }}
                                style={{ padding: "10px 16px", borderRadius: 8, background: T.text, color: "#fff", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 6 }}
                                onMouseEnter={(e: any) => e.currentTarget.style.background = T.textMid}
                                onMouseLeave={(e: any) => e.currentTarget.style.background = T.text}>
                                See all {c.permitCount} permits from this installer <ArrowRight size={13} />
                              </button>
                            )}
                            {onViewPermits && c.topCities[0] && (
                              <button
                                onClick={(e: any) => { e.stopPropagation(); onViewPermits(c.topCities[0].city, c.topCities[0].province); }}
                                style={{ padding: "10px 16px", borderRadius: 8, background: T.bg, color: T.text, border: `1.5px solid ${T.border}`, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}
                                onMouseEnter={(e: any) => e.currentTarget.style.borderColor = T.text}
                                onMouseLeave={(e: any) => e.currentTarget.style.borderColor = T.border}>
                                See all in {c.topCities[0].city} →
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ─── MARKETS MOVING (full width) ─── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, padding: "20px 22px", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TrendingUp size={16} color={T.primary} />
              <span style={{ fontFamily: "'Outfit'", fontSize: 13, fontWeight: 800, color: T.primary, textTransform: "uppercase", letterSpacing: "1.5px" }}>Markets moving</span>
            </div>
            <span style={{ fontSize: 11, color: T.textSub, fontWeight: 600 }}>
              {marketsRankedBy === 'growth' ? '% MoM growth' : 'top volume this month'}
            </span>
          </div>

          {loading ? (
            <div style={{ color: T.textSub, fontSize: 13 }}>Loading...</div>
          ) : marketsMoving.length === 0 ? (
            <div style={{ color: T.textSub, fontSize: 13, padding: "20px 0" }}>No active markets this period.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {marketsMoving.slice(0, 8).map((m: any, i: number) => (
                <div key={`${m.city}-${m.province}`}
                  onClick={() => onViewPermits && onViewPermits(m.city, m.province)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: i === 0 ? T.primaryLight : T.bgSub, cursor: onViewPermits ? "pointer" : "default", transition: "background 0.1s" }}
                  onMouseEnter={(e: any) => { if (onViewPermits) e.currentTarget.style.background = i === 0 ? "#FEE6CC" : T.border; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.background = i === 0 ? T.primaryLight : T.bgSub; }}>
                  <div style={{ width: 22, height: 22, borderRadius: 5, background: i === 0 ? T.primary : T.text, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.city}{m.province ? `, ${m.province}` : ''}</div>
                    <div style={{ fontSize: 11, color: T.textSub }}>{m.newThisMonth.toLocaleString()} new · {m.total.toLocaleString()} total</div>
                  </div>
                  {m.rankedBy === 'growth' && m.growthPct > 0 ? (
                    <div style={{ padding: "3px 10px", borderRadius: 6, background: T.greenBg, border: `1px solid ${T.greenBorder}`, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: T.greenDark }}>▲ {m.growthPct}%</span>
                    </div>
                  ) : (
                    <div style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 900, color: T.text, flexShrink: 0 }}>{m.newThisMonth.toLocaleString()}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ─── PRICING PULSE — 20-yr trends from 3.58M historical installs ─── */}
        {pricingTrends.length >= 3 && (() => {
          // Sort by year ascending
          const sorted = [...pricingTrends].sort((a: any, b: any) => a.year - b.year);
          const latest = sorted[sorted.length - 1];
          const earliest = sorted[0];
          const latestPpw = latest?.avg_price_per_watt || 0;
          const earliestPpw = earliest?.avg_price_per_watt || 0;
          const pctDrop = earliestPpw > 0 ? Math.round(((earliestPpw - latestPpw) / earliestPpw) * 100) : 0;
          const latestSize = latest?.avg_system_size_kw || 0;
          const storagePct = pricingOverview?.storage_adoption_pct || 0;

          // Sparkline points — normalize to 0-100 range over a 280×60 viewbox
          const W = 280, H = 60, PAD = 4;
          const ppwValues = sorted.map((t: any) => t.avg_price_per_watt || 0).filter((v: number) => v > 0);
          const minP = Math.min(...ppwValues);
          const maxP = Math.max(...ppwValues);
          const range = maxP - minP || 1;
          const points = sorted.map((t: any, i: number) => {
            const x = PAD + (i / Math.max(1, sorted.length - 1)) * (W - PAD * 2);
            const y = H - PAD - ((t.avg_price_per_watt - minP) / range) * (H - PAD * 2);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          }).join(" ");

          return (
            <div style={{ marginTop: 18, background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, padding: "20px 22px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Activity size={16} color={T.primary} />
                  <span style={{ fontFamily: "'Outfit'", fontSize: 13, fontWeight: 800, color: T.primary, textTransform: "uppercase", letterSpacing: "1.5px" }}>Pricing pulse</span>
                </div>
                <span style={{ fontSize: 11, color: T.textSub, fontWeight: 600 }}>
                  {earliest.year}–{latest.year} · 3.58M installs
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
                {/* Current $/W */}
                <div style={{ padding: "14px 16px", borderRadius: 10, background: T.bgSub, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", color: T.textSub, marginBottom: 6 }}>Avg $/W ({latest.year})</div>
                  <div style={{ fontFamily: "'Outfit'", fontSize: 26, fontWeight: 900, color: T.text, letterSpacing: "-0.8px", lineHeight: 1 }}>${latestPpw.toFixed(2)}</div>
                  {pctDrop > 0 && (
                    <div style={{ fontSize: 11, color: T.greenDark, fontWeight: 700, marginTop: 5 }}>▼ {pctDrop}% since {earliest.year}</div>
                  )}
                </div>
                {/* Avg system size */}
                <div style={{ padding: "14px 16px", borderRadius: 10, background: T.bgSub, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", color: T.textSub, marginBottom: 6 }}>Avg system size</div>
                  <div style={{ fontFamily: "'Outfit'", fontSize: 26, fontWeight: 900, color: T.text, letterSpacing: "-0.8px", lineHeight: 1 }}>{latestSize.toFixed(1)} <span style={{ fontSize: 14, color: T.textSub, fontWeight: 700 }}>kW</span></div>
                  <div style={{ fontSize: 11, color: T.textSub, fontWeight: 600, marginTop: 5 }}>residential, {latest.year}</div>
                </div>
                {/* Storage attach rate */}
                <div style={{ padding: "14px 16px", borderRadius: 10, background: T.bgSub, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", color: T.textSub, marginBottom: 6 }}>Storage attach</div>
                  <div style={{ fontFamily: "'Outfit'", fontSize: 26, fontWeight: 900, color: T.text, letterSpacing: "-0.8px", lineHeight: 1 }}>{storagePct.toFixed(1)}<span style={{ fontSize: 14, color: T.textSub, fontWeight: 700 }}>%</span></div>
                  <div style={{ fontSize: 11, color: T.textSub, fontWeight: 600, marginTop: 5 }}>of recent installs</div>
                </div>
              </div>

              {/* Sparkline of $/W over time */}
              <div style={{ padding: "12px 14px", borderRadius: 10, background: T.bgSub, border: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", color: T.textSub }}>$/W trend</div>
                  <div style={{ fontSize: 10, color: T.textFade, fontWeight: 600 }}>{earliest.year} ${earliestPpw.toFixed(2)} → {latest.year} ${latestPpw.toFixed(2)}</div>
                </div>
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 48, display: "block" }}>
                  <polyline points={points} fill="none" stroke={T.primary} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  {sorted.map((t: any, i: number) => {
                    const x = PAD + (i / Math.max(1, sorted.length - 1)) * (W - PAD * 2);
                    const y = H - PAD - ((t.avg_price_per_watt - minP) / range) * (H - PAD * 2);
                    return <circle key={t.year} cx={x} cy={y} r={i === sorted.length - 1 ? 3 : 1.5} fill={T.primary} />;
                  })}
                </svg>
              </div>
            </div>
        );
        })()}

        {/* ─── FRESH ROOFS — recent re-roofs as solar prospects ─── */}
        {freshRoofs.length > 0 && (
          <div style={{ marginTop: 18, background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, padding: "20px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Flame size={16} color={T.primary} />
                <span style={{ fontFamily: "'Outfit'", fontSize: 13, fontWeight: 800, color: T.primary, textTransform: "uppercase", letterSpacing: "1.5px" }}>Fresh roofs</span>
              </div>
              <span style={{ fontSize: 11, color: T.textSub, fontWeight: 600 }}>
                {currentCity ? `${currentCity}${currentState ? `, ${currentState}` : ""}` : currentState || "Nationwide"} · last 90 days
              </span>
            </div>

            {/* Pitch — why these matter */}
            <div style={{ padding: "10px 14px", background: T.primaryLight, borderRadius: 10, marginBottom: 14, border: `1px solid #FED7AA` }}>
              <div style={{ fontSize: 12, color: "#7C2D12", fontWeight: 700, lineHeight: 1.5 }}>
                {freshRoofsTotal.toLocaleString()} {freshRoofsTotal === 1 ? "homeowner" : "homeowners"} just re-roofed.
                <span style={{ fontWeight: 500, color: "#9A3412" }}> Clean substrate, demonstrated investment intent — prime solar pitch targets.</span>
              </div>
            </div>

            {/* Coverage transparency — tells reps what kind of data this is in their market */}
            {roofingCoverage?.territory && roofingCoverage.territory.total > 0 && (() => {
              const pct = roofingCoverage.territory.pct_named;
              const isAddressOnly = pct < 30;
              const isPartial = pct >= 30 && pct < 80;
              const isRich = pct >= 80;
              const topMarketsLabel = (roofingCoverage.topMarkets || []).slice(0, 4).map((m: any) => `${m.city} ${m.state}`).join(", ");
              return (
                <div style={{ padding: "9px 12px", borderRadius: 9, marginBottom: 14, background: isRich ? "#F0FDF4" : isAddressOnly ? "#F8FAFC" : "#FEFCE8", border: `1px solid ${isRich ? "#BBF7D0" : isAddressOnly ? T.border : "#FDE68A"}`, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: isRich ? "#16A34A" : isPartial ? "#CA8A04" : "#94A3B8", flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 11, lineHeight: 1.5, color: T.textMid }}>
                    {isRich && <><b style={{ color: "#15803D" }}>Full data</b> — {pct}% of records in this market include the roofer's name and contact info.</>}
                    {isPartial && <><b style={{ color: "#A16207" }}>Partial data</b> — {pct}% of records here include the roofer. Where missing, it's because the local jurisdiction doesn't publish it.</>}
                    {isAddressOnly && <><b>Address-only</b> in this market. Roofer info isn't published locally. Markets with full info: <span style={{ color: T.text, fontWeight: 600 }}>{topMarketsLabel}</span>.</>}
                  </div>
                </div>
              );
            })()}

            {/* List */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {freshRoofs.slice(0, 10).map((r: any) => {
                const dateStr = r.permit_date ? new Date(r.permit_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
                const ageStr = r.days_old != null
                  ? r.days_old < 1 ? "Today"
                    : r.days_old < 7 ? `${r.days_old}d ago`
                    : r.days_old < 30 ? `${Math.floor(r.days_old / 7)}w ago`
                    : `${Math.floor(r.days_old / 30)}mo ago`
                  : dateStr;
                const onCopy = (e: any) => {
                  e.stopPropagation();
                  const text = `${r.address}, ${r.city}${r.state ? ", " + r.state : ""}${r.zip ? " " + r.zip : ""}`;
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(text).then(() => {
                      setCopiedAddr(r.id);
                      setTimeout(() => setCopiedAddr(prev => prev === r.id ? null : prev), 1500);
                    }).catch(() => {});
                  }
                };
                return (
                  <div key={r.id}
                    onClick={onCopy}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, background: T.bgSub, cursor: "pointer", transition: "background 0.1s", border: `1px solid transparent` }}
                    onMouseEnter={(e: any) => e.currentTarget.style.background = T.border}
                    onMouseLeave={(e: any) => e.currentTarget.style.background = T.bgSub}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "#FFEDD5", border: "1px solid #FDBA74", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 14 }}>🏠</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.address}
                      </div>
                      <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
                        {r.city}{r.state ? `, ${r.state}` : ""}{r.zip ? ` ${r.zip}` : ""} · {ageStr}
                        {r.roofer_name && (
                          <span style={{ marginLeft: 6, color: T.textFade }}>· {r.roofer_name}</span>
                        )}
                      </div>
                    </div>
                    {r.squares != null && (
                      <div style={{ padding: "3px 8px", borderRadius: 5, background: "#FEF3C7", border: "1px solid #FCD34D", fontSize: 10, fontWeight: 800, color: "#92400E", flexShrink: 0, fontFamily: "'Outfit'", whiteSpace: "nowrap" }}>
                        {r.squares} sqs
                      </div>
                    )}
                    <div style={{ fontSize: 10, fontWeight: 700, color: copiedAddr === r.id ? T.greenDark : T.textSub, padding: "4px 9px", borderRadius: 6, background: copiedAddr === r.id ? T.greenBg : "transparent", border: `1px solid ${copiedAddr === r.id ? T.greenBorder : T.border}`, flexShrink: 0, transition: "all 0.15s", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {copiedAddr === r.id ? "Copied" : "Copy"}
                    </div>
                  </div>
                );
              })}
            </div>

            {freshRoofsTotal > 10 && (
              <div style={{ marginTop: 12, padding: "10px 14px", textAlign: "center", fontSize: 11, fontWeight: 600, color: T.textSub, background: T.bgOff, borderRadius: 8 }}>
                Showing top 10 of {freshRoofsTotal.toLocaleString()} fresh roofs in this period.
              </div>
            )}
          </div>
        )}

        {/* ─── ACTIVE ROOFERS — leaderboard, only renders when coverage is high ─── */}
        {rooferLeaderboard.length >= 3 && roofingCoverage?.territory?.pct_named >= 30 && (
          <div style={{ marginTop: 18, background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, padding: "20px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Activity size={16} color={T.primary} />
                <span style={{ fontFamily: "'Outfit'", fontSize: 13, fontWeight: 800, color: T.primary, textTransform: "uppercase", letterSpacing: "1.5px" }}>Active roofers</span>
              </div>
              <span style={{ fontSize: 11, color: T.textSub, fontWeight: 600 }}>
                {currentCity ? `${currentCity}${currentState ? `, ${currentState}` : ""}` : currentState || "Nationwide"} · last 180 days
              </span>
            </div>

            {/* Pitch */}
            <div style={{ padding: "10px 14px", background: T.bgOff, borderRadius: 10, marginBottom: 14, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 12, color: T.textMid, fontWeight: 600, lineHeight: 1.5 }}>
                Roofers actively working in your market.
                <span style={{ fontWeight: 500, color: T.textSub }}> Build referral partnerships — they hand you the homeowner pre-sold on home investment, you hand them the solar margin you don't service.</span>
              </div>
            </div>

            {/* Leaderboard rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {rooferLeaderboard.map((r: any, i: number) => {
                const contactKey = `${r.name}-${i}`;
                const contactPieces = [r.phone, r.email].filter(Boolean);
                const onCopyContact = (e: any) => {
                  e.stopPropagation();
                  if (contactPieces.length === 0) return;
                  const text = `${r.name}${r.phone ? ` · ${r.phone}` : ""}${r.email ? ` · ${r.email}` : ""}`;
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(text).then(() => {
                      setCopiedRooferContact(contactKey);
                      setTimeout(() => setCopiedRooferContact(prev => prev === contactKey ? null : prev), 1500);
                    }).catch(() => {});
                  }
                };
                return (
                  <div key={contactKey}
                    onClick={onCopyContact}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: T.bgSub, cursor: contactPieces.length > 0 ? "pointer" : "default", transition: "background 0.1s", border: `1px solid transparent` }}
                    onMouseEnter={(e: any) => { if (contactPieces.length > 0) e.currentTarget.style.background = T.border; }}
                    onMouseLeave={(e: any) => e.currentTarget.style.background = T.bgSub}>
                    {/* Rank */}
                    <div style={{ width: 26, height: 26, borderRadius: 7, background: i < 3 ? "#FEF3C7" : T.border, border: `1px solid ${i < 3 ? "#FCD34D" : T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 900, color: i < 3 ? "#92400E" : T.textSub, fontFamily: "'Outfit'" }}>{i + 1}</span>
                    </div>
                    {/* Name + contact */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.name}
                      </div>
                      <div style={{ fontSize: 11, color: T.textSub, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.phone || ""}{r.phone && r.email ? " · " : ""}{r.email || ""}
                        {!r.phone && !r.email && <span style={{ color: T.textFade, fontStyle: "italic" }}>contact info unavailable</span>}
                      </div>
                    </div>
                    {/* Permit count + copy */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: "'Outfit'" }}>
                        {r.permit_count} {r.permit_count === 1 ? "permit" : "permits"}
                      </div>
                      {contactPieces.length > 0 && (
                        <div style={{ fontSize: 9, fontWeight: 700, color: copiedRooferContact === contactKey ? T.greenDark : T.textSub, padding: "2px 6px", borderRadius: 5, background: copiedRooferContact === contactKey ? T.greenBg : "transparent", border: `1px solid ${copiedRooferContact === contactKey ? T.greenBorder : T.border}`, transition: "all 0.15s", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          {copiedRooferContact === contactKey ? "Copied" : "Copy"}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ FOOTER ═══ */}
        <div style={{ marginTop: 24, padding: "16px 22px", background: T.bg, borderRadius: 12, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <Sparkles size={16} color={T.primary} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>Intelligence refreshes every 10 minutes.</div>
            <div style={{ fontSize: 12, color: T.textSub }}>
              Live scan of municipal permit records across 1,500+ cities. Every permit above is a homeowner who just filed — call before install.
            </div>
          </div>
        </div>
      </div>

      {/* ═══ DRILL-DOWN MODAL — full permit list for one contractor ═══ */}
      {drillContractor && (
        <div
          onClick={() => setDrillContractor(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,15,26,0.55)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 40, zIndex: 1000,
          }}>
          <div
            onClick={(e: any) => e.stopPropagation()}
            style={{
              background: T.bg, borderRadius: 14, maxWidth: 920, width: "100%",
              maxHeight: "85vh", display: "flex", flexDirection: "column",
              boxShadow: "0 20px 60px rgba(15,15,26,0.35)",
              border: `1px solid ${T.border}`, overflow: "hidden",
            }}>
            {/* Modal header */}
            <div style={{ padding: "20px 28px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: T.red, marginBottom: 4 }}>Competitor radar · full list</div>
                <div style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 900, color: T.text, letterSpacing: "-0.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {drillContractor}
                </div>
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>
                  {drillLoading ? "Loading..." : `${drillLeads.length.toLocaleString()} permits · every address this installer filed`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {onAddToPipeline && drillLeads.length > 0 && (
                  <button
                    onClick={() => {
                      const leads = drillLeads.map((l: any) => ({
                        ...l, source: 'ai-insights', contractorName: drillContractor, stage: 'new',
                      }));
                      onAddToPipeline(leads);
                      setDrillContractor(null);
                    }}
                    style={{ padding: "10px 16px", borderRadius: 8, background: T.primary, color: "#fff", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 6 }}
                    onMouseEnter={(e: any) => e.currentTarget.style.background = T.primaryDark}
                    onMouseLeave={(e: any) => e.currentTarget.style.background = T.primary}>
                    Intercept all {drillLeads.length} <ArrowRight size={13} />
                  </button>
                )}
                <button
                  onClick={() => setDrillContractor(null)}
                  style={{ width: 36, height: 36, borderRadius: 8, background: T.bgSub, color: T.text, border: "none", fontSize: 18, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onMouseEnter={(e: any) => e.currentTarget.style.background = T.border}
                  onMouseLeave={(e: any) => e.currentTarget.style.background = T.bgSub}>
                  ×
                </button>
              </div>
            </div>

            {/* Modal body — scrollable list */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 20px" }}>
              {drillLoading ? (
                <div style={{ padding: 40, textAlign: "center", color: T.textSub, fontSize: 13 }}>Loading {drillContractor}'s full permit history...</div>
              ) : drillLeads.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: T.textSub, fontSize: 13 }}>No permits found for this contractor.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {drillLeads.map((lead: any, i: number) => {
                    const days = lead.daysOld ?? 0;
                    const fresh = days <= 30;
                    const isAdded = addedIds.has(lead.id);
                    return (
                      <div
                        key={lead.id || i}
                        onClick={() => handleAddSingleLead(lead, drillContractor)}
                        style={{
                          padding: "10px 14px", background: isAdded ? T.greenBg : T.bg,
                          borderRadius: 8, border: `1px solid ${isAdded ? T.greenBorder : T.borderSoft}`,
                          display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                          transition: "background 0.15s, border-color 0.15s",
                        }}
                        onMouseEnter={(e: any) => { if (!isAdded) e.currentTarget.style.borderColor = T.text; }}
                        onMouseLeave={(e: any) => { if (!isAdded) e.currentTarget.style.borderColor = T.borderSoft; }}>
                        <div style={{ width: 32, height: 32, borderRadius: 6, background: fresh ? T.redBgSolid : T.bgSub, color: fresh ? T.redDark : T.textMid, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, flexShrink: 0 }}>
                          {days <= 7 ? "⚡" : `${days}d`}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lead.address}</div>
                          <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
                            {lead.city}{lead.province ? `, ${lead.province}` : ''} · filed {days}d ago
                            {lead.permitValue > 0 && fmtMoney(lead.permitValue) && <span> · {fmtMoney(lead.permitValue)}</span>}
                          </div>
                        </div>
                        {isAdded ? (
                          <div style={{ padding: "6px 12px", borderRadius: 6, background: T.green, color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                            <Check size={12} /> Added
                          </div>
                        ) : (
                          <div style={{ padding: "6px 12px", borderRadius: 6, background: T.text, color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                            <Plus size={12} /> Intercept
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
